<template>
  <div class="d-flex ga-3 stage">
    <div>
      <v-btn
        variant="tonal"
        density="compact"
        rounded="circle"
        class="font-weight-bold sequence"
        :class="`sequence--${stageStatus}`"
        :color="sequenceColor"
        size="small"
        :icon="stageStatus === 'completed' ? 'mdi-check' : 'mdi-circle-medium'">
      </v-btn>
    </div>

    <div class="w-100">
      <div v-if="stage.participants.length === 1">
        <div
          v-for="participant in stage.participants"
          :key="participant.id"
          class="d-flex justify-space-between align-center">
          <div>
            <div class="font-weight-bold font-size-15">
              <span class="mr-1">
                {{ getPersonName(participant.user) }}
              </span>
              <span class="text-disabled">{{ stage.type.name }}</span>
            </div>
            <div class="text-disabled">{{ participant.user.post }}</div>
            <div
              v-if="participant.resultId"
              :class="{
                'text-success': participant.resultId === 1,
                'text-error': participant.resultId === 2,
              }">
              {{ participant.result.name }}
              {{
                new CustomDate(participant.resultAt).toString({ time: true })
              }}
            </div>

            <div v-else-if="stage.deadlineAt" class="text-warning">
              Граничний термін
              {{ new CustomDate(stage.deadlineAt).toString() || 'Необмежений' }}
            </div>
          </div>
          <div>
            <v-chip v-if="stageStatus === 'next'" color="grey">
              <b>Очікує</b>
            </v-chip>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="d-flex align-center ga-3 justify-space-between mb-3">
          <div class="font-weight-bold font-size-15">
            {{ stage.type.name }}
          </div>

          <v-chip v-if="stageStatus === 'current'" color="info">
            <b>
              {{ stage.participants.filter(p => p.resultId).length }} з
              {{ stage.participants.length }}
            </b>
          </v-chip>
          <v-chip v-else-if="stageStatus === 'next'" color="grey">
            <b>Очікує</b>
          </v-chip>
        </div>

        <div v-if="stage.id === currentStage?.id" class="mb-3">
          <div class="font-weight-bold font-size-12 text-blue-darken-3">
            {{ stage.participants.filter(p => p.resultId).length }} з
            {{ stage.participants.length }}
          </div>
          <v-progress-linear
            :model-value="
              100 /
              (stage.participants.length /
                stage.participants.filter(p => p.resultId).length)
            "
            rounded
            color="blue-darken-3"
            height="5"></v-progress-linear>
        </div>

        <div class="pl-5">
          <div
            v-for="participant in stage.participants"
            :key="participant.id"
            class="d-flex ga-3 mb-2">
            <div>
              <v-btn
                v-if="participant.resultId"
                class="sequence sequence--completed"
                variant="tonal"
                density="compact"
                rounded="circle"
                color="success"
                icon
                size="x-small">
                <v-icon icon="mdi-check" size="x-small"></v-icon>
              </v-btn>
              <v-btn
                v-else
                class="sequence"
                border
                variant="tonal"
                density="compact"
                rounded="circle"
                size="x-small"
                icon></v-btn>
            </div>
            <div class="w-100">
              <div class="font-weight-bold font-size-15">
                {{ getPersonName(participant.user) }}
              </div>
              <div class="text-disabled">{{ participant.user.post }}</div>
              <div
                v-if="participant.resultId"
                :class="{
                  'text-success': participant.resultId === 1,
                  'text-error': participant.resultId === 2,
                }">
                {{ participant.result.name }}
                {{
                  new CustomDate(participant.resultAt).toString({ time: true })
                }}
              </div>

              <div v-else-if="stage.deadlineAt" class="text-warning">
                Граничний термін
                {{
                  new CustomDate(stage.deadlineAt).toString() || 'Необмежений'
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DocumentRouteStage } from '@/utils/types/documentRoute'
import { computed } from 'vue'
import { useDocument } from '../../document'
import { CustomDate, getPersonName } from 'best-modules/utils'

defineOptions({
  name: 'RouteTrackerStage',
})
const props = defineProps<{
  stage: DocumentRouteStage
}>()

const { currentStage } = useDocument()

const stageStatus = computed<'completed' | 'current' | 'next'>(() => {
  if (!props.stage.startedAt) {
    return 'next'
  }

  if (currentStage.value?.id === props.stage.id) {
    return 'current'
  }

  return 'completed'
})

const sequenceColor = computed(() => {
  switch (stageStatus.value) {
    case 'completed':
      return 'success'
    case 'current':
      return 'info'
    case 'next':
      return 'grey'
    default:
      return 'grey'
  }
})
</script>

<style lang="scss" scoped>
.sequence {
  border-width: 2px;
  border-style: solid;

  &--next {
    border-color: rgb(var(--v-theme-grey));
  }
  &--completed {
    border-color: rgb(var(--v-theme-success));
  }
  &--current {
    border-color: rgb(var(--v-theme-info));
  }
}

.stage {
  position: relative;

  &:not(.stage:last-child):after {
    content: '';
    position: absolute;
    width: 2px;
    height: calc(100% - 12px);
    background: #dfdfdf;
    top: 24px;
    left: 11px;
  }
}
</style>
